/**
 * Supabase client singleton.
 *
 * Initialised with the anon key at build time via VITE_SUPABASE_URL +
 * VITE_SUPABASE_ANON_KEY. The anon key is safe to ship to browsers —
 * RLS policies enforce access at the database layer.
 *
 * When env vars are missing (e.g. local dev without a Supabase stack)
 * we expose a null client and callers detect that via
 * `isSupabaseEnabled()`. Anonymous flows still work because the API
 * layer's anon-session cookie provides identity.
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

let client: SupabaseClient | null = null;

if (SUPABASE_URL && SUPABASE_ANON_KEY) {
  client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storageKey: "hideaway.auth",
    },
    global: {
      headers: {
        "X-Client-Info": "hideaway-web/5.0",
      },
    },
  });
}

export function getSupabase(): SupabaseClient | null {
  return client;
}

export function isSupabaseEnabled(): boolean {
  return client !== null;
}

export async function getAccessToken(): Promise<string | null> {
  if (!client) return null;
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? null;
}
